import React, { useState } from 'react';
import { todayIST, yesterdayIST } from '../utils/dates';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { companyService, getWorkspaceId, setWorkspace } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  HomeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { BrandLogo } from './Logo';

export const Layout = () => {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Company users: list of workspaces for the header switcher
  const { data: wsData } = useQuery(
    'companyWorkspaces',
    companyService.getWorkspaces,
    { enabled: !!user?.isCompany, staleTime: 60 * 1000 }
  );
  const workspaces = wsData?.data?.data?.workspaces || [];

  const switchWorkspace = (id) => {
    if (!id || id === getWorkspaceId()) return;
    const ws = workspaces.find(w => String(w._id) === String(id));
    if (ws && ws.status !== 'active') return;
    setWorkspace(id, ws?.name || '');
    window.location.assign('/dashboard');
  };

  // India days, whatever timezone this computer is on (utils/dates)
  const todayStr     = todayIST();
  const yesterdayStr = yesterdayIST();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: HomeIcon,
      current: location.pathname === '/dashboard',
      permission: () => hasPermission('dashboard', 'view'),
    },
    {
      name: 'Leads',
      href: '/leads',
      icon: BuildingOfficeIcon,
      current: location.pathname.startsWith('/leads'),
      permission: () => hasPermission('leads', 'read'),
      children: [
        { name: 'Show Enquiries',        href: '/leads' },
        { name: "Today's Enquiries",     href: `/leads?enquiryDate=${todayStr}` },
        { name: "Yesterday's Enquiries", href: `/leads?enquiryDate=${yesterdayStr}` },
        { name: 'Pending Follow-ups',    href: '/leads?pendingFollowups=true' },
        { name: 'Duplicate Leads',       href: '/leads?duplicates=true' },
        { name: 'Recycle Bin',           href: '/leads/recycle-bin' },
      ],
    },
    {
      name: 'Report',
      href: '/reports/funnel',
      icon: ChartBarIcon,
      current: location.pathname.startsWith('/reports'),
      // Team-wide analytics — not for sales_executive (their own leads.read scope is too narrow to be useful here)
      permission: () => hasPermission('leads', 'read') && user?.role !== 'sales_executive',
      children: [
        { name: 'Funnel Report', href: '/reports/funnel' },
        { name: 'Lost Report',   href: '/reports/lost' },
        { name: 'TAT Report',    href: '/reports/tat' },
        // Per-dealer/branch breakdown within the current workspace only.
        { name: 'Group Report',  href: '/reports/group' },
        // Cross-workspace roll-up — only meaningful for company users, who can
        // see every workspace under their company (leaves the current
        // workspace context, navigating to the Company Console's report page).
        ...(user?.isCompany ? [{ name: 'Group Report (All Workspaces)', href: '/company/group-report' }] : []),
      ],
    },
    {
      name: 'Users',
      href: '/users',
      icon: UserGroupIcon,
      current: location.pathname === '/users',
      permission: () => hasPermission('users', 'read'),
    },
    {
      name: 'Role Permissions',
      href: '/role-permissions',
      icon: ShieldCheckIcon,
      current: location.pathname === '/role-permissions',
      permission: () => user?.role === 'super_admin' || user?.role === 'admin',
    },
    {
      name: 'Settings',
      href: '/settings/brands',
      icon: Cog6ToothIcon,
      current: location.pathname.startsWith('/settings'),
      permission: () => hasPermission('brands', 'view') || hasPermission('facebook_integration', 'view') || hasPermission('google_integration', 'view') || hasPermission('website_integration', 'view') || user?.role === 'super_admin' || user?.role === 'admin',
      children: [
        { name: 'Brands & Models',        href: '/settings/brands',   permission: () => hasPermission('brands', 'view') },
        { name: 'Facebook Integration',   href: '/settings/facebook', permission: () => hasPermission('facebook_integration', 'view') },
        // Super admin only — company users act as one inside a workspace
        { name: 'Lead Distribution',      href: '/settings/lead-distribution', permission: () => user?.role === 'super_admin' },
        { name: 'WhatsApp Integration',   href: '/settings/whatsapp', permission: () => hasPermission('whatsapp_integration', 'view') },
        { name: 'WhatsApp Templates',     href: '/settings/whatsapp-templates', permission: () => ['super_admin', 'dealer'].includes(user?.role) && hasPermission('whatsapp_integration', 'view') },
        { name: 'Email Notifications',    href: '/settings/email',   permission: () => user?.role === 'super_admin' },
        { name: 'Google Ads Integration', href: '/settings/google',   permission: () => hasPermission('google_integration', 'view') },
        { name: 'Website Integration',    href: '/settings/website',  permission: () => hasPermission('website_integration', 'view') },
        { name: 'CRM Integrations',       href: '/settings/crm',      permission: () => user?.role === 'super_admin' },
        { name: 'Backups',                href: '/settings/backups',  permission: () => user?.role === 'super_admin' },
      ].filter(c => c.permission()),
    },
  ].filter(item => {
    if (!item.permission()) return false;
    // Hide parent if it has children defined but all were filtered out
    if (item.children !== undefined && item.children.length === 0) return false;
    return true;
  });

  // Company-only: manage workspaces
  if (user?.isCompany) {
    navigation.push({
      name: 'Workspaces',
      href: '/company',
      icon: Squares2X2Icon,
      current: location.pathname.startsWith('/company'),
      permission: () => true,
    });
  }

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleConfig = {
    company:         { label: 'Company',        cls: 'bg-primary-100 text-primary-700' },
    super_admin:     { label: 'Super Admin',    cls: 'bg-red-100 text-red-700' },
    admin:           { label: 'Admin',          cls: 'bg-amber-100 text-amber-700' },
    dealer:          { label: 'Dealer',         cls: 'bg-blue-100 text-blue-700' },
    sales_executive: { label: 'Sales Executive', cls: 'bg-emerald-100 text-emerald-700' },
  };
  const role = roleConfig[user?.role] || { label: user?.role, cls: 'bg-gray-100 text-gray-600' };

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-[260px] w-full bg-gray-900 shadow-2xl">
            <div className="absolute top-3 -right-12">
              <button
                className="flex items-center justify-center h-10 w-10 rounded-full bg-white/10 text-white hover:bg-white/20"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent navigation={navigation} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0 print:hidden">
        <div className="flex flex-col w-60">
          <SidebarContent navigation={navigation} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">

        {/* Top header */}
        <header className="relative z-10 flex-shrink-0 flex h-16 bg-white border-b border-gray-200 shadow-sm print:hidden">
          <button
            className="lg:hidden px-4 text-gray-500 hover:text-gray-700 focus:outline-none"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex-1 flex items-center justify-between px-5">
            {/* Breadcrumb / page context */}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <HomeIcon className="h-4 w-4" />
              <span>/</span>
              <span className="text-gray-800 font-medium capitalize">
                {({ leads: 'Enquiries', users: 'Users', dashboard: 'Dashboard', settings: 'Settings', profile: 'Profile', 'role-permissions': 'Role Permissions' }[location.pathname.split('/')[1]] || location.pathname.split('/')[1] || 'Dashboard')}
              </span>
            </div>

            {/* Right controls */}
            <div className="flex items-center gap-3">
              {/* Profile */}
              <div className="relative">
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-gray-800 leading-tight">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-400 leading-tight">{role.label}</p>
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-900">{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                      <span className={`mt-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${role.cls}`}>
                        {role.label}
                      </span>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setProfileDropdownOpen(false)}
                      >
                        <UserCircleIcon className="h-4 w-4 text-gray-400" />
                        Your Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Company workspace switcher */}
        {user?.isCompany && (
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-5 py-2 bg-primary-50 border-b border-primary-200">
            <div className="flex items-center gap-2 text-sm text-primary-800">
              <span className="font-medium">Workspace:</span>
              <select
                value={getWorkspaceId()}
                onChange={(e) => switchWorkspace(e.target.value)}
                className="text-sm font-semibold text-primary-900 bg-white border border-primary-300 rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400"
              >
                {workspaces.map(w => (
                  <option key={w._id} value={w._id} disabled={w.status !== 'active'}>
                    {w.name}{w.status !== 'active' ? ' (suspended)' : ''}
                  </option>
                ))}
              </select>
              <span className="text-primary-500">(company access)</span>
            </div>
            <Link
              to="/company"
              className="text-sm font-medium text-primary-700 hover:text-primary-900 underline underline-offset-2"
            >
              Manage Workspaces
            </Link>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          {/* The analytics dashboard paints its own dark, full-width board */}
          <div className={location.pathname === '/dashboard' ? 'min-h-full' : 'py-6 px-5 sm:px-8 max-w-[1400px] mx-auto'}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

const SidebarContent = ({ navigation }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [openParent, setOpenParent] = useState(() => {
    if (location.pathname.startsWith('/leads'))    return 'Leads';
    if (location.pathname.startsWith('/reports'))  return 'Report';
    if (location.pathname.startsWith('/settings')) return 'Settings';
    return null;
  });

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Logo */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-800">
        <BrandLogo />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = item.current;

          if (item.children) {
            const isOpen = openParent === item.name;
            return (
              <div key={item.name}>
                <button
                  onClick={() => setOpenParent(isOpen ? null : item.name)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-accent-500 text-white'
                      : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 opacity-60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                </button>

                {isOpen && (
                  <div className="ml-8 mt-1 mb-1 space-y-0.5">
                    {item.children.map(child => {
                      const childPath   = child.href.split('?')[0];
                      const childSearch = child.href.includes('?') ? '?' + child.href.split('?')[1] : '';
                      const isChildActive = location.pathname === childPath && location.search === childSearch;
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                            isChildActive
                              ? 'bg-primary-500/20 text-primary-300 font-medium'
                              : 'text-gray-500 hover:bg-gray-800 hover:text-gray-300'
                          }`}
                        >
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-accent-500 text-white shadow-sm'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-3 py-3 border-t border-gray-800">
        <p className="text-xs text-gray-600 text-center">LMS v1.0</p>
      </div>
    </div>
  );
};
